/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ReactNode, useState } from 'react'
import RequestHandler from '../lib/request_handler'

type NpsSurveyFormProps = {
  shopId: number
  isPcView: boolean
}

export default function NpsSurveyModal({
  shopId,
  isPcView = false,
}: NpsSurveyFormProps): ReactNode {
  const [hidden, setHidden] = useState<boolean>(false)
  const [score, setScore] = useState<number>(null)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [answered, setAnswered] = useState<boolean>(false)

  const handleSelectedScore = (value: number) => {
    setScore(value)
    setDisabled(false)
  }

  const submitScoreAndComment = async () => {
    const textareaValue: string =
      ((document.getElementById('npsComment') as HTMLFormElement)?.value as string) ?? ''
    const params: { shop_id: number; score: number; comment: string; action_type: string } = {
      shop_id: shopId,
      score: score,
      comment: textareaValue,
      action_type: 'post_score',
    }

    await postNpsData(params)
    setAnswered(true)
    setTimeout(() => {
      setHidden(true)
    }, 2000)
  }

  const cancel = async () => {
    await postNpsData({ shop_id: shopId, score: 0, comment: '', action_type: 'cancel' })
    setHidden(true)
  }

  const postNpsData = async (params: {
    shop_id: number
    score: number
    comment: string
    action_type: string
  }) => {
    const defaultParams = { platform_type: isPcView ? 'pcweb' : 'spweb' }
    const handler = new RequestHandler(
      `/nps_loggings`,
      'POST',
      { ...defaultParams, ...params },
      true
    )

    try {
      await handler.request()
    } catch (err) {
      console.error(err)
    }
  }

  if (hidden) {
    return null
  }

  if (answered) {
    return (
      <div css={modalContainer}>
        <section css={[npsSurveySection, modal]}>
          <div css={[npsSurveySectionTitle, textColorBlack, alignCenter]}>
            フィードバックにご協力ください
          </div>
          <div css={thanksForAnswerArea}>
            <div css={thanksForAnswerText}>ご協力ありがとうございました。</div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div css={modalContainer}>
      <section css={[npsSurveySection, modal]}>
        <button css={closeButton} onClick={cancel}>
          &times;
        </button>
        <div css={[npsSurveySectionTitle, textColorBlack, alignCenter]}>
          フィードバックにご協力ください
        </div>
        <div css={npsSurveySectionContent}>
          <p css={textColorBlack}>
            あなたはくふうトクバイを家族や友人にすすめる可能性はどのくらいありますか？
          </p>
          <div css={selectScoreButtonArea}>
            <div css={[alignLeft, textSizeSmall]}>全くない</div>
            <div css={scoreButtonArea(6)}>
              {[0, 1, 2, 3, 4, 5].map((s) => (
                <div
                  id={`npsSurveyScoreButton-${s}`}
                  key={`${s}`}
                  css={s === score ? scoreButtonAttention : scoreButton}
                  onClick={() => {
                    handleSelectedScore(s)
                  }}
                >
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <div css={scoreButtonArea(5)}>
              {[6, 7, 8, 9, 10].map((s) => (
                <div
                  id={`npsSurveyScoreButton-${s}`}
                  key={`${s}`}
                  css={s === score ? scoreButtonAttention : scoreButton}
                  onClick={() => {
                    handleSelectedScore(s)
                  }}
                >
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <div css={[alignRight, textSizeSmall]}>可能性が高い</div>
          </div>
        </div>
        <textarea
          id={'npsComment'}
          cols={40}
          rows={5}
          maxLength={140}
          placeholder="サービスへのご意見・ご要望をお聞かせください"
        />
        <div className={'center new_bargain_photo_comment_wrapper'}>
          <button
            className={'button attention full_width_button'}
            disabled={disabled}
            onClick={submitScoreAndComment}
            css={isPcView ? [margin10, answerButton] : [answerButton]}
          >
            回答を送信する
          </button>
          <button className={'button full_width_button'} onClick={cancel}>
            今回は答えない
          </button>
        </div>
      </section>
    </div>
  )
}

const npsSurveySection = css`
  margin: 30px 8px 8px 8px;
  padding: 8px;
  background-color: rgb(255, 255, 255);
`
const npsSurveySectionContent = css`
  padding: 8px;
`
const selectScoreButtonArea = css`
  margin: 10px 0;
`
const scoreButtonArea = (colSize: number) => css`
  display: grid;
  grid-template-columns: repeat(${colSize}, 17%);
  row-gap: 24px;
  justify-content: center;
  margin: 5px 0;
`
const scoreButton = css`
  background-color: rgba(0, 0, 0, 0.54);
  border-radius: 50%;
  text-align: center;
  color: rgb(255, 255, 255);
  width: 40px;
  height: 40px;
  margin: auto;
  cursor: pointer;
  > span {
    font-size: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
`
const scoreButtonAttention = css(scoreButton, {
  backgroundColor: 'rgba(247, 8, 22, 1)',
})
const npsSurveySectionTitle = css`
  padding: 8px;
  border-bottom: rgba(0, 0, 0, 0.095) 1px solid;
`
const textColorBlack = css`
  color: rgba(0, 0, 0, 0.87);
`
const textSizeSmall = css`
  font-size: 12px;
`
const alignCenter = css`
  text-align: center;
`
const alignRight = css`
  text-align: right;
`
const alignLeft = css`
  text-align: left;
`
const thanksForAnswerArea = css`
  padding: 8px;
`
const thanksForAnswerText = css(
  textColorBlack,
  alignCenter,
  `
  margin: 30px;
`
)
const modalContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`
const modal = css`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  max-width: 100%;
  position: relative;
`
const closeButton = css`
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 24px;
`
const margin10 = css`
  margin: 10px;
`
const answerButton = css`
  &:disabled {
    box-shadow: none;
    background-image: none;
    background-color: rgba(0, 0, 0, 0.12);
    color: rgba(0, 0, 0, 0.2) !important;
    border: none;
    cursor: default;
    opacity: 1;
    text-shadow: none;
  }
`
